import React from "react";
import { Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DoorFrontOutlinedIcon from "@mui/icons-material/DoorFrontOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";

import BasicLayoutNew from "../components/BasicLayoutNew";
import { MainLogo } from "../../../components/Icons/MainLogo";
import { APP_NAME } from "../../../constants";
import bgImage from "assets/images/city.jpg";
import phoneMapImage from "assets/images/nexus-6-singleMap.png";
import {
  authFeatureChipSx,
  authPanelSx,
  authSurfaceSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "../authStyles";

const benefits = [
  {
    icon: <StorefrontOutlinedIcon />,
    title: "More foot traffic",
    text: "Make your business discoverable to people nearby who are actively looking for a clean restroom.",
  },
  {
    icon: <DoorFrontOutlinedIcon />,
    title: "Access control",
    text: "Manage availability, door-code settings, premium-only access, and operational rules by location.",
  },
  {
    icon: <AnalyticsOutlinedIcon />,
    title: "Useful insights",
    text: "Track visits, ratings, issues, and trends so restroom access becomes measurable instead of invisible.",
  },
  {
    icon: <GroupsOutlinedIcon />,
    title: "Team ready",
    text: "Support single-location hosts, multi-location operators, corporate admins, and local managers.",
  },
];

const controls = [
  "Manage one or many host locations",
  "Assign corporate or location-level admins",
  "View visits, ratings, reports, and access issues",
  "Suspend or moderate user access when needed",
  "Use the web dashboard and mobile app with the same account",
];

const getStarted = [
  "Create your business account",
  "Confirm the email address before logging in",
  "Add your business location and access details",
  "Manage performance from the host portal",
];

const LearnMore: React.FC = () => {
  return (
    <BasicLayoutNew image={bgImage} wide>
      <MDBox width="100%">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={7}>
            <Card sx={{ ...authPanelSx, height: "100%" }}>
              <MDBox
                sx={{
                  p: { xs: 3, md: 5 },
                  minHeight: "100%",
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 3, md: 4 },
                  alignItems: "center",
                }}
              >
                <MDBox flex={1}>
                  <MDBox sx={authFeatureChipSx}>
                    <ManageAccountsOutlinedIcon sx={{ fontSize: 18 }} />
                    Host Growth
                  </MDBox>
                  <MDTypography
                    variant="h1"
                    fontWeight={900}
                    mt={2}
                    mb={1.5}
                    sx={{ color: "#ffffff", lineHeight: 1.02 }}
                  >
                    Turn your restroom into a business asset.
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{
                      color: "rgba(248, 251, 255, 0.78)",
                      fontWeight: 500,
                      lineHeight: 1.75,
                      maxWidth: 620,
                    }}
                  >
                    {APP_NAME} helps businesses convert restroom access into foot traffic,
                    stronger customer experience, and operational insights that hosts can
                    manage from the web dashboard or mobile app.
                  </MDTypography>

                  <MDBox display="flex" gap={1.25} mt={3} flexWrap="wrap">
                    <MDButton
                      component={Link}
                      to="/authentication/sign-up"
                      variant="gradient"
                      color="info"
                      sx={primaryButtonSx}
                      endIcon={<ArrowForwardOutlinedIcon />}
                    >
                      Register Your Business
                    </MDButton>
                    <MDButton
                      component={Link}
                      to="/authentication/sign-in"
                      variant="outlined"
                      color="light"
                      sx={{
                        ...secondaryButtonSx,
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.22)",
                      }}
                      startIcon={<ArrowBackOutlinedIcon />}
                    >
                      Back to Login
                    </MDButton>
                  </MDBox>
                </MDBox>

                <MDBox
                  component="img"
                  src={phoneMapImage}
                  alt={`${APP_NAME} map preview`}
                  sx={{
                    width: { xs: 170, sm: 215, md: 235 },
                    maxWidth: { xs: "70%", md: "34%" },
                    filter: "drop-shadow(0 24px 36px rgba(0, 0, 0, 0.34))",
                    borderRadius: "22px",
                  }}
                />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ ...authSurfaceSx, height: "100%" }}>
              <MDBox p={{ xs: 3, md: 4 }} height="100%" display="flex" flexDirection="column">
                <MDBox textAlign="center">
                  <MainLogo style={{ width: "190px", maxWidth: "74%" }} />
                  <MDTypography variant="h3" color="dark" fontWeight={900} mt={1}>
                    Why hosts join
                  </MDTypography>
                  <MDTypography variant="body2" color="text" fontWeight="medium" mt={1}>
                    Restrooms are requested constantly. The portal turns that demand into
                    something your business can control, measure, and benefit from.
                  </MDTypography>
                </MDBox>

                <MDBox mt={3}>
                  {["Discoverability", "Cleanliness signals", "Operational control"].map((item) => (
                    <MDBox
                      key={item}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={1.25}
                      sx={{
                        p: 1.5,
                        borderRadius: "15px",
                        backgroundColor: "rgba(8, 120, 237, 0.08)",
                        border: "1px solid rgba(20, 41, 62, 0.10)",
                      }}
                    >
                      <CheckCircleOutlineOutlinedIcon sx={{ color: "#2eae1f", fontSize: 19 }} />
                      <MDTypography variant="button" color="dark" fontWeight="bold">
                        {item}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0}>
          {benefits.map((benefit) => (
            <Grid item xs={12} md={6} lg={3} key={benefit.title}>
              <Card sx={{ ...authSurfaceSx, height: "100%" }}>
                <MDBox p={2.5}>
                  <MDBox
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "14px",
                      backgroundColor: "rgba(8, 120, 237, 0.10)",
                      color: "#0878ed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    {benefit.icon}
                  </MDBox>
                  <MDTypography variant="h6" color="dark" fontWeight={900}>
                    {benefit.title}
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1} sx={{ lineHeight: 1.65 }}>
                    {benefit.text}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} mt={0}>
          <Grid item xs={12} lg={6}>
            <Card sx={{ ...authSurfaceSx, height: "100%" }}>
              <MDBox p={{ xs: 3, md: 4 }}>
                <MDTypography variant="h3" color="dark" fontWeight={900}>
                  Host controls and permissions
                </MDTypography>
                <MDTypography variant="body2" color="text" fontWeight="medium" mt={1} mb={2.5}>
                  Hosts keep control over location settings, team access, availability,
                  and the data that matters most.
                </MDTypography>
                {controls.map((item) => (
                  <MDBox key={item} display="flex" gap={1.25} alignItems="flex-start" mb={1.5}>
                    <CheckCircleOutlineOutlinedIcon sx={{ color: "#2eae1f", fontSize: 20, mt: 0.1 }} />
                    <MDTypography variant="body2" color="dark" fontWeight="bold">
                      {item}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ ...authSurfaceSx, height: "100%" }}>
              <MDBox p={{ xs: 3, md: 4 }}>
                <MDTypography variant="h3" color="dark" fontWeight={900}>
                  Getting started
                </MDTypography>
                <MDTypography variant="body2" color="text" fontWeight="medium" mt={1} mb={2.5}>
                  The web portal and mobile app use the same host account, so setup is
                  straightforward and your information stays synced.
                </MDTypography>
                {getStarted.map((item, index) => (
                  <MDBox key={item} display="flex" gap={1.5} alignItems="flex-start" mb={1.5}>
                    <MDBox
                      sx={{
                        width: 30,
                        height: 30,
                        minWidth: 30,
                        borderRadius: "12px",
                        backgroundColor: "rgba(8, 120, 237, 0.10)",
                        color: "#0878ed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                      }}
                    >
                      {index + 1}
                    </MDBox>
                    <MDTypography variant="body2" color="dark" fontWeight="bold" mt={0.5}>
                      {item}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ ...authPanelSx, mt: 3 }}>
          <MDBox
            p={{ xs: 3, md: 4 }}
            display="flex"
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            gap={2}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <MDBox>
              <MDTypography variant="h3" fontWeight={900} sx={{ color: "#ffffff" }}>
                Ready to add your business?
              </MDTypography>
              <MDTypography
                variant="body2"
                mt={1}
                sx={{ color: "rgba(248, 251, 255, 0.72)", fontWeight: 500 }}
              >
                Create the host account, confirm your email, then add your first location.
              </MDTypography>
            </MDBox>
            <MDButton
              component={Link}
              to="/authentication/sign-up"
              variant="gradient"
              color="info"
              sx={primaryButtonSx}
              endIcon={<ArrowForwardOutlinedIcon />}
            >
              Register Your Business
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </BasicLayoutNew>
  );
};

export default LearnMore;
